const gradeOrder = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];

export const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (
    !trimmed ||
    trimmed === "0000-00-00" ||
    trimmed === "0000-00-00 00:00:00"
  ) {
    return null;
  }
  const normalized = trimmed.replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    parsed.setHours(23, 59, 59, 999);
  }
  return parsed;
};

export const gradeSortValue = (code) => {
  const value = String(code ?? "").trim();
  if (!value) return Number.MAX_SAFE_INTEGER;
  const index = gradeOrder.indexOf(value);
  if (index !== -1) return index;
  const numeric = Number.parseInt(value, 10);
  if (!Number.isNaN(numeric)) {
    return numeric + gradeOrder.length;
  }
  return Number.MAX_SAFE_INTEGER;
};

const normalizeCertifiedValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "0000-00-00 00:00:00") {
      return null;
    }
    return value;
  }
  return value;
};

const statusPriority = (status) => {
  if (status === undefined || status === null) return 0;
  const value = Number(status);
  if (value === 1) return 3;
  if (value === 2) return 2;
  if (value === 0) return 1;
  return 0;
};

const hasDownloadFlag = (grade) => {
  if (!grade) return false;
  if (grade.download_date) return true;
  const downloaded = grade.downloaded;
  return downloaded === "1" || downloaded === 1 || downloaded === true;
};

export const selectPreferredGrade = (existing, candidate) => {
  if (!existing) return candidate;
  if (!candidate) return existing;

  const existingPriority = statusPriority(existing.status ?? existing.status1);
  const candidatePriority = statusPriority(candidate.status ?? candidate.status1);

  if (candidatePriority > existingPriority) {
    return candidate;
  }
  if (existingPriority > candidatePriority) {
    return existing;
  }

  const candidateDownload = hasDownloadFlag(candidate) ? 1 : 0;
  const existingDownload = hasDownloadFlag(existing) ? 1 : 0;
  if (candidateDownload > existingDownload) {
    return candidate;
  }
  if (existingDownload > candidateDownload) {
    return existing;
  }

  const existingEnd = parseDateValue(existing.end_date ?? existing.implementation_end_date);
  const candidateEnd = parseDateValue(candidate.end_date ?? candidate.implementation_end_date);

  if (!existingEnd && candidateEnd) {
    return candidate;
  }
  if (existingEnd && candidateEnd) {
    if (candidateEnd.getTime() > existingEnd.getTime()) {
      return candidate;
    }
  }

  return existing;
};

export const normalizeTeacherImplementations = (details = [], fallbackTeacherId = null) => {
  const grouped = new Map();

  details.forEach((item) => {
    if (!item?.implementation_id) {
      return;
    }
    const key = item.implementation_id;
    if (!grouped.has(key)) {
      grouped.set(key, {
        implementation_id: item.implementation_id,
        start_date: item.start_date ?? "",
        end_date: item.end_date ?? "",
        school_id: item.school_id ?? null,
        invoice_number: item.invoice_number ?? "",
        certify_implementation: item.certify_implementation ?? null,
        certified: item.certified ?? null,
        num_of_students: item.num_of_students ?? null,
        org_name: item.school ?? "",
        school: item.school ?? "",
        customer_tag: item.customer_tag ?? null,
        status: item.status ?? null,
        status1: item.status1 ?? null,
        grade_codes: [],
        user_id: item.user_id ?? fallbackTeacherId,
        session_id: item.session_id ?? null,
      });
    }

    const target = grouped.get(key);
    target.start_date = target.start_date || item.start_date || "";
    target.end_date = target.end_date || item.end_date || "";
    const certifiedValue = normalizeCertifiedValue(item.certified);
    target.certified = target.certified || certifiedValue || null;
    if (item.num_of_students != null && item.num_of_students !== "") {
      target.num_of_students = item.num_of_students;
    }
    if (item.status === "1" || item.status === 1) {
      target.status = "1";
    } else if (target.status == null) {
      target.status = item.status ?? null;
    }

    if (item.status1 === "1" || item.status1 === 1) {
      target.status1 = "1";
    } else if (target.status1 == null) {
      target.status1 = item.status1 ?? null;
    }

    const gradeCode = item.grade_code ?? item.grades ?? null;
    if (gradeCode) {
      const downloadedFlag = item.download_date ? "1" : "0";
      const gradeStatus = item.status ?? item.grade_status ?? null;
      target.grade_codes.push({
        grade_code: gradeCode,
        downloaded: downloadedFlag,
        certified: certifiedValue,
        download_date: item.download_date ?? null,
        status: gradeStatus,
        status1: item.status1 ?? null,
        start_date: item.start_date ?? null,
        end_date: item.end_date ?? null,
        session_id: item.session_id ?? target.session_id ?? null,
        user_id: item.user_id ?? fallbackTeacherId,
        school_id: item.school_id ?? target.school_id ?? null,
        implementation_end_date: item.end_date ?? target.end_date ?? null,
      });
    }
  });

  return Array.from(grouped.values()).map((entry) => {
    const gradeMap = new Map();
    entry.grade_codes.forEach((grade) => {
      if (!grade?.grade_code) return;
      const code = String(grade.grade_code).trim();
      if (!code) return;
      const candidate = { ...grade, grade_code: code };
      const existing = gradeMap.get(code);
      gradeMap.set(code, selectPreferredGrade(existing, candidate));
    });
    const uniqueGrades = Array.from(gradeMap.values()).sort((a, b) => {
      const aKey = gradeSortValue(a.grade_code);
      const bKey = gradeSortValue(b.grade_code);
      if (aKey !== bKey) return aKey - bKey;
      return String(a.grade_code ?? "").localeCompare(String(b.grade_code ?? ""));
    });
    return {
      ...entry,
      grade_codes: uniqueGrades,
      grade_records: uniqueGrades,
    };
  });
};
